import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GLASS_CARD_TILE } from '@/shared/ui-class';
import {
  valueBandTileComparison,
  valueBandTileValue,
  type ValueBandRead,
} from './ValueBand.transform';

export function ValueBand({
  read,
  projectName,
  showMoney = true,
}: {
  read: ValueBandRead;
  projectName: string;
  /** #731: the floor seats (Operator, Control room) read the tiles' non-money basis. */
  showMoney?: boolean;
}) {
  const tiles = [
    {
      id: 'recovery',
      label: 'Recovery gap',
      value: valueBandTileValue(read, 'recovery', showMoney),
      basis: `Zn ${read.recoveryZnActualPct.toFixed(1)}% vs ${read.recoveryZnDesignPct.toFixed(1)}% · Pb ${read.recoveryPbActualPct.toFixed(1)}% vs ${read.recoveryPbDesignPct.toFixed(1)}% ${read.recoveryBasis}`,
      comparison: valueBandTileComparison(read, 'recovery', showMoney),
      href: read.recoveryHref,
      labelLink: `Recovery gap at ${projectName} · open filtered recovery cases`,
    },
    {
      id: 'throughput',
      label: 'Throughput headroom',
      value: valueBandTileValue(read, 'throughput', showMoney),
      basis: `${read.throughputCurrentStph.toFixed(1)} stph now → ${read.throughputCeilingStph.toFixed(1)} stph ceiling`,
      comparison: valueBandTileComparison(read, 'throughput', showMoney),
      href: read.throughputHref,
      labelLink: `Throughput opportunity · open capacity for ${projectName}`,
    },
    {
      id: 'uptime',
      label: 'Uptime',
      value: valueBandTileValue(read, 'uptime', showMoney),
      basis: `1 availability point · ${read.uptimeAvailabilityPct.toFixed(1)}% Month 4 · ${read.uptimeYear1ScheduledHours.toLocaleString('en-AU')} scheduled h in Year 1`,
      comparison: valueBandTileComparison(read, 'uptime', showMoney),
      href: read.uptimeHref,
      labelLink: `Uptime opportunity · open downtime for ${projectName}`,
    },
  ] as const;

  return (
    <section
      id="walkthrough-value-band"
      aria-label="Annual value at stake"
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
    >
      {tiles.map((tile) => (
        <Link
          key={tile.id}
          to={tile.href}
          aria-label={tile.labelLink}
          data-value-tile={tile.id}
          className={`${GLASS_CARD_TILE} group flex min-h-[6rem] min-w-0 flex-col justify-between px-3 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)]`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted">
              {tile.label}
            </span>
            <ArrowUpRight className="size-3.5 shrink-0 text-muted transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
          </div>
          <p className="mt-1 text-xl font-semibold leading-none tabular-nums text-ink-primary">
            {tile.value}
          </p>
          <div className="mt-1 space-y-0.5 text-[10px] leading-snug text-muted">
            <p>{tile.basis}</p>
            <p className="font-medium text-ink-secondary">{tile.comparison}</p>
          </div>
        </Link>
      ))}
    </section>
  );
}
